import { useState, useEffect } from 'react';
import { Save, Key, RefreshCw, Eye, EyeOff } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import apiService from '../services/api';
import toast from 'react-hot-toast';

const Settings = () => {
  const { user, updateUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [apiKeys, setApiKeys] = useState(null);
  const [showSecret, setShowSecret] = useState(false);
  const [showWebhookSecret, setShowWebhookSecret] = useState(false);
  const [profile, setProfile] = useState({
    business_name: '',
    email: '',
    phone: '',
    address: '',
    website: '',
    webhook_payin_url: '',
    webhook_payout_url: ''
  });

  useEffect(() => {
    loadProfile();
    loadApiKeys();
  }, []);

  const loadProfile = async () => {
    try {
      const response = await apiService.getMerchantProfile();
      if (response.success) {
        const profileData = response.data;
        setProfile({
          business_name: profileData.business_name || '',
          email: profileData.email || '',
          phone: profileData.phone || '',
          address: profileData.address || '',
          website: profileData.website || '',
          webhook_payin_url: profileData.webhook_payin_url || '',
          webhook_payout_url: profileData.webhook_payout_url || ''
        });
      }
    } catch (error) {
      console.error('Failed to load profile:', error);
    }
  };

  const loadApiKeys = async () => {
    try {
      const response = await apiService.getApiKeys();
      if (response.success) {
        setApiKeys(response.data);
      }
    } catch (error) {
      console.error('Failed to load API keys:', error);
    }
  };

  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      const response = await apiService.updateMerchantProfile(profile);
      if (response.success) {
        updateUser(response.data);
        toast.success('Profile updated successfully');
      }
    } catch (error) {
      console.error('Failed to update profile:', error);
      toast.error('Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  const handleWebhookUpdate = async () => {
    try {
      const response = await apiService.setWebhookUrl({
        webhook_payin_url: profile.webhook_payin_url,
        webhook_payout_url: profile.webhook_payout_url
      });
      if (response.success) {
        toast.success('Webhook URLs updated successfully');
      }
    } catch (error) {
      console.error('Failed to update webhook:', error);
      toast.error('Failed to update webhook URLs');
    }
  };

  const regenerateApiSecret = async () => {
    if (!confirm('Are you sure you want to regenerate your API secret? This will invalidate the current API secret and webhook secret.')) {
      return;
    }

    try {
      const response = await apiService.regenerateApiSecret();
      if (response.success) {
        setApiKeys(response.data);
        toast.success('API secret regenerated successfully');
        toast('Please update your integration with the new secret', {
          duration: 5000,
          icon: '⚠️'
        });
      }
    } catch (error) {
      console.error('Failed to regenerate API secret:', error);
      toast.error('Failed to regenerate API secret');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold text-gray-900">Settings</h1>

      {/* Profile Settings */}
      <div className="card p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">
          Business Profile
        </h2>
        <form onSubmit={handleProfileUpdate} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Business Name
              </label>
              <input
                type="text"
                value={profile.business_name}
                onChange={(e) => setProfile({ ...profile, business_name: e.target.value })}
                className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Email
              </label>
              <input
                type="email"
                value={profile.email}
                onChange={(e) => setProfile({ ...profile, email: e.target.value })}
                className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Phone
              </label>
              <input
                type="tel"
                value={profile.phone}
                onChange={(e) => setProfile({ ...profile, phone: e.target.value })}
                className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Website
              </label>
              <input
                type="url"
                value={profile.website}
                onChange={(e) => setProfile({ ...profile, website: e.target.value })}
                className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                placeholder="https://yourwebsite.com"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Address
            </label>
            <textarea
              value={profile.address}
              onChange={(e) => setProfile({ ...profile, address: e.target.value })}
              rows={3}
              className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
            />
          </div>

          <div className="flex justify-end">
            <button
              type="submit"
              disabled={loading}
              className="btn-primary inline-flex items-center"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Saving...
                </>
              ) : (
                <>
                  <Save size={16} className="mr-2" />
                  Save Profile
                </>
              )}
            </button>
          </div>
        </form>
      </div>

      {/* API Keys */}
      <div className="card p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900">
            API Credentials
          </h2>
          <button
            onClick={regenerateApiSecret}
            className="btn-secondary inline-flex items-center"
          >
            <RefreshCw size={16} className="mr-2" />
            Regenerate Secret
          </button>
        </div>

        {apiKeys && (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                API Key
              </label>
              <div className="flex items-center space-x-2">
                <input
                  type="text"
                  value={apiKeys.api_key}
                  readOnly
                  className="flex-1 bg-gray-50 border border-gray-300 rounded px-3 py-2 text-sm"
                />
                <button
                  onClick={() => copyToClipboard(apiKeys.api_key)}
                  className="btn-secondary"
                >
                  Copy
                </button>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                API Secret
              </label>
              <div className="flex items-center space-x-2">
                <div className="flex-1 relative">
                  <input
                    type={showSecret ? 'text' : 'password'}
                    value={apiKeys.api_secret}
                    readOnly
                    className="w-full bg-gray-50 border border-gray-300 rounded px-3 py-2 pr-10 text-sm"
                  />
                  <button
                    type="button"
                    onClick={() => setShowSecret(!showSecret)}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  >
                    {showSecret ? (
                      <EyeOff size={16} className="text-gray-400" />
                    ) : (
                      <Eye size={16} className="text-gray-400" />
                    )}
                  </button>
                </div>
                <button
                  onClick={() => copyToClipboard(apiKeys.api_secret)}
                  className="btn-secondary"
                >
                  Copy
                </button>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Webhook Secret
              </label>
              {apiKeys.webhook_secret ? (
                <div className="flex items-center space-x-2">
                  <div className="flex-1 relative">
                    <input
                      type={showWebhookSecret ? 'text' : 'password'}
                      value={apiKeys.webhook_secret}
                      readOnly
                      className="w-full bg-gray-50 border border-gray-300 rounded px-3 py-2 pr-10 text-sm"
                    />
                    <button
                      type="button"
                      onClick={() => setShowWebhookSecret(!showWebhookSecret)}
                      className="absolute inset-y-0 right-0 pr-3 flex items-center"
                    >
                      {showWebhookSecret ? (
                        <EyeOff size={16} className="text-gray-400" />
                      ) : (
                        <Eye size={16} className="text-gray-400" />
                      )}
                    </button>
                  </div>
                  <button
                    onClick={() => copyToClipboard(apiKeys.webhook_secret)}
                    className="btn-secondary"
                  >
                    Copy
                  </button>
                </div>
              ) : (
                <p className="text-sm text-gray-600">
                  Not generated yet. Click "Regenerate Secret" to create one (this also replaces your API secret).
                </p>
              )}
              <p className="mt-1 text-xs text-gray-500">
                Used to verify the X-Webhook-Signature header on webhooks we send you. Regenerating the API secret also replaces this secret.
              </p>
            </div>

            <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <Key className="h-5 w-5 text-yellow-400" />
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-yellow-800">
                    Keep your credentials secure
                  </h3>
                  <div className="mt-2 text-sm text-yellow-700">
                    <ul className="list-disc pl-5 space-y-1">
                      <li>Never share your API secret with anyone</li>
                      <li>Store credentials securely in environment variables</li>
                      <li>Regenerate immediately if compromised</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Webhook Settings */}
      <div className="card p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">
          Webhook Configuration
        </h2>
        <div className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Payin Webhook URL
            </label>
            <input
              type="url"
              value={profile.webhook_payin_url}
              onChange={(e) => setProfile({ ...profile, webhook_payin_url: e.target.value })}
              className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
              placeholder="https://yoursite.com/webhook/payin"
            />
            <p className="mt-1 text-xs text-gray-500">
              We'll send payin/payment collection status updates to this URL
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Payout Webhook URL
            </label>
            <input
              type="url"
              value={profile.webhook_payout_url}
              onChange={(e) => setProfile({ ...profile, webhook_payout_url: e.target.value })}
              className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
              placeholder="https://yoursite.com/webhook/payout"
            />
            <p className="mt-1 text-xs text-gray-500">
              We'll send payout/withdrawal status updates to this URL
            </p>
          </div>

          <div className="flex justify-end">
            <button
              onClick={handleWebhookUpdate}
              className="btn-primary"
            >
              Update Webhooks
            </button>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
            <h4 className="text-sm font-medium text-blue-800 mb-2">
              Webhook Events
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-blue-700">
              <div>
                <p className="font-medium mb-1">Payin Events:</p>
                <ul className="space-y-1 ml-4">
                  <li>• payin.success - Payment completed</li>
                  <li>• payin.failed - Payment failed</li>
                  <li>• payin.pending - Payment processing</li>
                  <li>• payin.cancelled - Payment cancelled</li>
                </ul>
              </div>
              <div>
                <p className="font-medium mb-1">Payout Events:</p>
                <ul className="space-y-1 ml-4">
                  <li>• payout.success - Payout completed</li>
                  <li>• payout.failed - Payout failed</li>
                  <li>• payout.pending - Payout processing</li>
                  <li>• payout.queued - Payout queued</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Settings;